import "./index.scss";
import React, { useState, useEffect } from "react";
import {
    generateResolver,
    Modal,
    Notification,
    STATUS_TYPES,
    yup,
} from "dyl-components";
import { useDispatch, useSelector } from "react-redux";
import { VALIDATORS } from "dyl-components";
import authActions from "actions/auth/auth";
import assignActions from "actions/assign";
import { QAOptionsUtils } from "utils";
import userActions from "actions/user";
import companyActions from "actions/company";
import { FormProvider, useForm } from "react-hook-form";
import { Form, Segment } from "semantic-ui-react";
import UserForm from "./UserForm";
import contactActions from "actions/contact";
import contactEmailActions from "actions/contact_email";
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";

const AddUserModal = ({ contact_id, open, onClose, customer, company, checkOpenSeats=()=>{}, reportsToDisabled }) => {
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const { unusedUserExtensions } = useSelector((state) => state.pbx_config);
    
    const { isCreating, roles, contactOptions, isCheckingDuplicates } =
        useSelector((state) => ({
            isCheckingDuplicates:
                state.user.isFindingUserByEmail ||
                state.contact_duplicates.isCheckingDuplicates,
            isCreating:
                state.contact_email.isUpdatingEmail ||
                state.contact.contactBeingUpdated ||
                state.users.isCreatingUser ||
                state.auth.isCreatingUser ||
                state.team.teamWhereUsersAreBeingAdded ||
                state.company.isCreatingCompany || 
                state.contact_email.isCreatingEmail,
            roles: state.roles.rolesNames.map(({ access_role_id, access_role_name, is_owner_role }) => ({
                key: access_role_id,
                value: access_role_id,
                text: access_role_name,
                is_owner_role
            })),
            ...(contact_id
                ? {
                      contactOptions: state.account.contactsForPinning.map(
                          (contact) => {
                              const {
                                  first_name,
                                  last_name,
                                  phone,
                                  email,
                                  location,
                              } = contact;
                              const name = `${first_name} ${last_name}`;
                              return {
                                  key: contact.id,
                                  value: contact.id,
                                  text: name,
                                  first_name,
                                  last_name,
                                  phone: phone.phone,
                                  phone_id: phone.id,
                                  email: email.email,
                                  email_type: email?.email_type,
                                  email_id: email.id,
                                  location,
                              };
                          }
                      ),
                  }
                : {}),
        }));

    const canAddUserExtension = useSelector(
        (state) => state.subscription.hasOpenSeats?.has_phone
    );
    const methods = useForm({
        defaultValues: {
            first_name: "",
            last_name: "",
            email: "",
            email_type: "",
            street: "",
            suite: "",
            city: "",
            state: "",
            zipcode: "",
            timezone: "America/New_York",
            work_number: "",
            extension: canAddUserExtension ? "" : undefined,
            cell_number: "",
            report_to: 0,
            teams: [],
            profile_type: null,
            job_title: "",
            ...(contact_id
                ? (() => {
                      const contact = contactOptions.find(
                          (contact) => contact.value === contact_id
                      );
                      return {
                          contact_id: contact_id,
                          cell_number: contact?.phone,
                          email: contact?.email,
                          email_type: contact?.email_type,
                          first_name: contact?.first_name,
                          last_name: contact?.last_name,
                          street: contact?.location?.street || "",
                          suite: contact?.location?.additional_street || "",
                          city: contact?.location?.city || "",
                          state: contact?.location?.state || "",
                          zipcode: contact?.location?.zip || "",
                      };
                  })()
                : {}),
        },
        resolver: generateResolver({
            first_name: VALIDATORS.FIRST_NAME().required(
                "This field is required"
            ),
            last_name: VALIDATORS.LAST_NAME().required(
                "This field is required"
            ),
            email: VALIDATORS.EMAIL_ADDRESS().required(
                "This field is required"
            ),
            street: VALIDATORS.STREET_ADDRESS().nullable(true),
            suite: yup
                .string()
                .no_excessive_whitespaces()
                .noemoji()
                .minlength(2)
                .maxlength(12).nullable(true),
            city: VALIDATORS.TOWN().nullable(true),
            state: VALIDATORS.STATE().required("This field is required"),
            zipcode: VALIDATORS.US_POSTAL_CODE().nullable(true),
            work_number: VALIDATORS.PHONE_NUMBER().nullable(true),
            extension: canAddUserExtension ? VALIDATORS.PHONE_NUMBER_EXTENSION().required("This field is required") : undefined,
            cell_number: VALIDATORS.PHONE_NUMBER(),
            profile_type: yup.number().test(
                "is-valid-profile-type",
                "This field is required",
                (value) => value !== null && value !== undefined && value !== 0
            ),
        }),
        mode: "onChange",
    });

    const {
        formState: { isValid , isDirty },
        reset,
        handleSubmit,
        setValue,
        getValues
    } = methods;

    useEffect(() => {
        if (roles?.length > 0 && getValues("profile_type") === null) {
            const defaultRole = roles.find((role) =>
                customer ? role.is_owner_role : undefined
            )?.value || 0;
            if (defaultRole !== undefined) {
                setValue("profile_type", defaultRole);
            }
        }
     
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roles, customer]);

    const dispatch = useDispatch();

    const onAddLogin = async (data) => {
        const {
            first_name,
            last_name,
            email,
            street,
            suite,
            city,
            state,
            zipcode,
            timezone,
            work_number,
            extension,
            cell_number,
            report_to,
            teams,
            profile_type,
            job_title,
        } = data;

        const qaExpirationTime = QAOptionsUtils.getQueryOption("qattl") || null; //allows QA to change the expiration of a new user link
        try {
            if (contact_id && customer) {
                try {
                    await dispatch(
                        companyActions.createCompany(
                            { ...company, timezone },
                            null,
                            company?.account_id
                        )
                    );
                } catch (e) {
                    if (e.Code !== 409) {
                        console.log(e);
                        throw e;
                    }
                }
                await dispatch(
                    contactActions.updateContact(contact_id, {
                        first_name,
                        last_name,
                        job_title,
                    })
                );
                const contact = contactOptions.find(
                    (contact) => contact.value === contact_id
                );
                if (contact.email !== data.email) {
                    if (!contact.email) {
                        await dispatch(contactEmailActions.addContactEmail([
                            {
                                main: true,
                                email: data.email,
                                email_type: data.email_type
                            }
                        ], null, contact_id))
                    } else {
                        await dispatch(
                            contactEmailActions.updateContactEmail(contact_id, { email: data.email, main: true, email_type: data.email_type }, null, contact.email_id)
                        );
                    }
                }
            }
            await dispatch(
                authActions.createUser(
                    {
                        first_name,
                        last_name,
                        email,
                        street,
                        suite,
                        city,
                        state,
                        zipcode,
                        timezone,
                        work_number,
                        extension: extension ? unusedUserExtensions.find((ext) => ext.extension_id === Number(extension))?.extension : undefined,
                        extension_id: extension,
                        cell_number,
                        report_to,
                        label: "Office",
                        main: true,
                        profile_type,
                        job_title,
                        teams,
                        ttl: qaExpirationTime,
                    },
                    { customer_id: company?.account_id, isAdmin: customer }
                )
            );
            Notification.alert(
                "Successfully added user!",
                STATUS_TYPES.SUCCESS,
                true
            );
            reset({
                first_name: "",
                last_name: "",
                email: "",
                street: "",
                suite: "",
                city: "",
                state: "",
                zipcode: "",
                timezone: "America/New_York",
                work_number: "",
                extension: "",
                cell_number: "",
                report_to: 0,
                teams: [],
                profile_type: null,
                job_title: "",
            });
            onClose();
            dispatch(assignActions.readUserAccounts({ limit: 25, page: 1 }));
            checkOpenSeats();
            dispatch(userActions.getProductLimits());
            if (contact_id && customer) {
                dispatch(userActions.getSummarizedUsers(company.account_id));
            }
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to add user", STATUS_TYPES.ERROR, true);
        }
    };
    return (
        <React.Fragment>
            <Modal open={open} onClose={() => {
                isDirty
                    ? setIsConfirmationOpen(true)
                    : onClose();
            }} size="small">
            <Modal.Header>Add New User</Modal.Header>
            <Modal.Content>
                <Form loading={isCreating} noValidate>
                    <FormProvider {...methods}>
                        <UserForm customer={customer} contact_id={contact_id} canAddUserExtension={canAddUserExtension} reportsToDisabled={reportsToDisabled} />
                    </FormProvider>
                </Form>
            </Modal.Content>
            <Modal.Actions
                hasSaveButton
                onSave={handleSubmit(onAddLogin)}
                saveDisabled={!isValid || isCheckingDuplicates || isCreating}
                saveOptions={{ loading: isCreating }}
            />
        </Modal>
            <FullScreenModalPopUp
                header={"Changes not saved"}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    onClose();
                    setIsConfirmationOpen(false);
                    methods.reset();
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </React.Fragment>
    );
};

const AddUserModalContainer = ({ contact_id, open, onClose, customer, company, checkOpenSeats=()=>{}, reportsToDisabled }) => {
    const isReadingRoles = useSelector((state) => state.roles.isReadingRoles);
    if (isReadingRoles) {
        return (
            <Modal open={open} onClose={onClose} size="small">
                <Modal.Header>Add New User</Modal.Header>
                <Modal.Content>
                    <Segment basic loading />
                </Modal.Content>
            </Modal>
        )
    }
    const props = { contact_id, open, onClose, customer, company, checkOpenSeats, reportsToDisabled };
    return <AddUserModal {...props} />
}

export default AddUserModalContainer;
